/**
 * Cross-subdomain persistence for the visitor's chosen language.
 *
 * Mirrors the cookie strategy used for Supabase auth in each app's
 * `services/supabase.ts`: on *.rehub.studio we write a cookie scoped to
 * `.rehub.studio` so a language picked in one app carries to the others.
 * Everywhere else (localhost, Vercel previews) we fall back to
 * localStorage only.
 */

import { LANG_STORAGE_KEY, isSupportedLanguage, type LanguageCode } from './config';

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function shouldUseSharedCookie(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'rehub.studio' || host.endsWith('.rehub.studio');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = (document.cookie || '').split('; ');
  for (const part of parts) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const attrs = '; Domain=.rehub.studio; Path=/; SameSite=Lax; Secure';
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${attrs}; Max-Age=${ONE_YEAR_SECONDS}`;
}

/** Read the stored language, preferring the shared cookie then localStorage. */
export function readStoredLanguage(): LanguageCode | null {
  const cookieValue = readCookie(LANG_STORAGE_KEY);
  if (isSupportedLanguage(cookieValue)) return cookieValue;

  try {
    const local = typeof window !== 'undefined' ? window.localStorage.getItem(LANG_STORAGE_KEY) : null;
    if (isSupportedLanguage(local)) return local;
  } catch {
    // localStorage may be unavailable (private mode, SSR) - ignore.
  }
  return null;
}

/** Persist the chosen language to the shared cookie (where applicable) and localStorage. */
export function writeStoredLanguage(lang: LanguageCode): void {
  if (shouldUseSharedCookie()) writeCookie(LANG_STORAGE_KEY, lang);
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Ignore storage failures.
  }
}
